/**
 * リリースエントリ（`content/releases/*.yml`）の並べ方・選び方・検査（#348）。
 *
 * ページ（`pages/releases.vue`）とフィード（`server/routes/releases.xml.ts`）の両方が使う。
 * server からは `~/utils/releases` で読む（Nuxt 4 では server の `~` も `app/` を指す）。
 *
 * 設計の正本: `doc/architecture/update_history.md`
 */

export const RELEASE_KINDS = ['new', 'improve', 'fix'] as const
export type ReleaseKind = typeof RELEASE_KINDS[number]

export const RELEASE_KIND_LABELS: Record<ReleaseKind, string> = {
  new: '新機能',
  improve: '改善',
  fix: '修正',
}

/**
 * 投稿の末尾に付けるハッシュタグ。works.xml / adventures.xml と揃える（`lib/src/gennai/handlers/feeds.py`）。
 * YAML には書かせず、フィード側で固定で付ける。
 */
export const RELEASE_HASHTAGS = '#CodeAndMagic #プログラミング'

/**
 * Karasu の既定テンプレート `{{summary}}\n\n{{url}}` で、本文（＝フィードの description）に
 * 使える字数。`truncate_to_140` は 140 から「テンプレートの改行 2 字 + URL 23 字（X は t.co で固定
 * カウント）」を引いた分を超えると**本文の末尾から切る**（karasu-app `render_post_text`）。
 */
export const KARASU_DESCRIPTION_BUDGET = 140 - 2 - 23

/**
 * X にそのまま載る `summary` の上限。description は `summary + "\n\n" + ハッシュタグ` なので、
 * これを超えると Karasu が**末尾のハッシュタグから切り落とす**。
 * `RELEASE_SUMMARY_MAX + 2 + RELEASE_HASHTAGS.length <= KARASU_DESCRIPTION_BUDGET` をテストで固定している。
 */
export const RELEASE_SUMMARY_MAX = 90

export interface ReleaseEntry {
  kind: ReleaseKind
  text: string
  link?: string
}

export interface Release {
  version: string
  date: string
  summary: string
  entries: ReleaseEntry[]
  draft?: boolean
}

const SEMVER = /^(\d+)\.(\d+)\.(\d+)$/
const DATE = /^\d{4}-\d{2}-\d{2}$/

function parseSemver(version: string): [number, number, number] {
  const m = SEMVER.exec(version)
  if (!m) throw new Error(`SemVer ではない版: ${version}`)
  return [Number(m[1]), Number(m[2]), Number(m[3])]
}

/** SemVer の比較。a が新しければ正。 */
export function compareSemver(a: string, b: string): number {
  const pa = parseSemver(a)
  const pb = parseSemver(b)
  for (let i = 0; i < 3; i++) {
    if (pa[i] !== pb[i]) return pa[i]! - pb[i]!
  }
  return 0
}

/**
 * 新しい順に並べる。**`date` ではなく `version` で並べる** —— 同じ日に 2 本出したときに
 * 順序が決まらず、フィードの「最新」がぶれるため。
 */
export function sortReleases<T extends Pick<Release, 'version'>>(releases: readonly T[]): T[] {
  return [...releases].sort((a, b) => compareSemver(b.version, a.version))
}

/**
 * フィードに出す 1 件。`draft` を除いた中で版がいちばん新しいもの。
 *
 * **フィードは常にこの 1 件だけを出す。** Karasu の `allow_repeat: false` は未投稿の item を
 * 1 日 1 件ずつ選ぶので、複数載せると最新を出した翌日から過去の版をさかのぼって全部投稿する。
 * 「最新を 1 回だけ」は Karasu の設定ではなく、フィードの item 数で表現している。
 */
export function latestPublishedRelease<T extends Pick<Release, 'version' | 'draft'>>(
  releases: readonly T[],
): T | undefined {
  return sortReleases(releases.filter(r => !r.draft))[0]
}

/** ページ内アンカー（`/releases#v2-50-0`）。ドットは id に使えるが URL で紛れるので避ける。 */
export function releaseAnchor(version: string): string {
  return `v${version.replaceAll('.', '-')}`
}

/**
 * フィードの guid。**URL にしてはいけない** —— リンク先は `/releases` の 1 ページなので、
 * URL を guid にすると全リリースで同じ値になる。Karasu の重複防止は guid で全フィード横断に
 * 効くため、最初の 1 回の後は永久に投稿されなくなる（doc/operations/feed_verification.md）。
 */
export function releaseGuid(version: string): string {
  return `release:${version}`
}

/** フィードの description（＝ X の投稿本文。Karasu は `skip_summarize: true` でそのまま使う）。 */
export function releaseFeedDescription(summary: string): string {
  return `${summary.trim()}\n\n${RELEASE_HASHTAGS}`
}

/** `2026-11-10` → `11/10`。`Date` を通すとタイムゾーンで日付がずれうるので文字列で切る。 */
export function releaseShortDate(date: string): string {
  const [, m, d] = date.split('-')
  return `${Number(m)}/${Number(d)}`
}

/** `2026-11-10` → `2026.11.10`（`/news` の表記に揃える）。 */
export function releaseDisplayDate(date: string): string {
  return date.replaceAll('-', '.')
}

/**
 * 全エントリを検査し、問題を文字列の配列で返す（空なら正常）。
 *
 * フィードの prerender で呼び、1 件でもあればビルドを落とす。YAML を手で書くので、
 * 版の書き間違い（`2.50` など）や `summary` の書きすぎは X に出る前に止めたい。
 */
export function validateReleases(releases: readonly Partial<Release>[]): string[] {
  const problems: string[] = []
  const seen = new Set<string>()
  for (const r of releases) {
    const label = r.version ?? '(version なし)'
    if (!r.version || !SEMVER.test(r.version)) {
      problems.push(`${label}: version が SemVer（x.y.z）ではない`)
    }
    else if (seen.has(r.version)) {
      problems.push(`${label}: version が重複している`)
    }
    else {
      seen.add(r.version)
    }
    if (!r.date || !DATE.test(r.date)) {
      problems.push(`${label}: date が YYYY-MM-DD ではない`)
    }
    const summary = (r.summary ?? '').trim()
    if (!summary) {
      problems.push(`${label}: summary が空`)
    }
    else if ([...summary].length > RELEASE_SUMMARY_MAX) {
      problems.push(`${label}: summary が ${RELEASE_SUMMARY_MAX} 字を超えている（${[...summary].length} 字）`)
    }
    if (!r.entries || r.entries.length === 0) {
      problems.push(`${label}: entries が空`)
    }
    for (const e of r.entries ?? []) {
      if (!RELEASE_KINDS.includes(e.kind)) {
        problems.push(`${label}: entries の kind が ${RELEASE_KINDS.join(' / ')} のどれでもない（${e.kind}）`)
      }
      if (!e.text?.trim()) {
        problems.push(`${label}: entries に text が空のものがある`)
      }
    }
  }
  return problems
}
